/**
 * SystemDox MCP preview — part of the Connect landing (/connect).
 * Calls the same console APIs the MCP server proxies, formats the result
 * with the exact formatters the MCP tools use (see mcpPreview.ts), and
 * renders it so teams can see precisely what a connected assistant reads.
 */

import { useEffect, useState } from 'react';
import { Terminal, AlertTriangle, Check, Copy, Cpu, ListChecks, ScrollText, Sparkles, type LucideIcon } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { techStackApi } from '@/services/techStackApi';
import { guardrailsApi } from '@/services/guardrailsApi';
import { rulesPromptsApi } from '@/services/rulesPromptsApi';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { cn } from '@/lib/utils';
import {
  formatTechStackResponse,
  formatGuardrailsResponse,
  formatProjectRulesResponse,
  formatPromptTemplatesResponse,
} from './mcpPreview';

interface McpToolDef {
  name: string;
  description: string;
  icon: LucideIcon;
  activeClass: string;
  fetch: () => Promise<string>;
}

const MCP_TOOLS: McpToolDef[] = [
  {
    name: 'get_tech_stack',
    description: 'The configured technology stack with versions and notes.',
    icon: Cpu,
    activeClass: 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    fetch: () => techStackApi.list().then((s) => formatTechStackResponse(s, window.location.origin)),
  },
  {
    name: 'get_checks',
    description: 'Banned patterns, required patterns, and coding rules.',
    icon: ListChecks,
    activeClass: 'border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300',
    fetch: () => guardrailsApi.listEntries().then((g) => formatGuardrailsResponse(g, window.location.origin)),
  },
  {
    name: 'get_project_rules',
    description: 'Team-defined principles grouped by category.',
    icon: ScrollText,
    activeClass: 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
    fetch: () => rulesPromptsApi.listRules().then((r) => formatProjectRulesResponse(r, window.location.origin)),
  },
  {
    name: 'get_prompt_templates',
    description: 'Reusable prompts the AI should apply when generating content.',
    icon: Sparkles,
    activeClass: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
    fetch: () => rulesPromptsApi.listPromptTemplates().then((t) => formatPromptTemplatesResponse(t, window.location.origin)),
  },
];

type ToolState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; text: string };

export function McpPreviewSection() {
  const [activeTool, setActiveTool] = useState(MCP_TOOLS[0].name);
  const [results, setResults] = useState<Record<string, ToolState>>({});
  const [view, setView] = useState<'rendered' | 'raw'>('rendered');
  const [copied, setCopied] = useState(false);

  const active = MCP_TOOLS.find((t) => t.name === activeTool)!;
  const state = results[activeTool];

  useEffect(() => {
    if (results[activeTool]) return;
    let cancelled = false;
    setResults((prev) => ({ ...prev, [activeTool]: { status: 'loading' } }));
    active.fetch().then(
      (text) => {
        if (!cancelled) setResults((prev) => ({ ...prev, [activeTool]: { status: 'loaded', text } }));
      },
      () => {
        if (!cancelled) setResults((prev) => ({ ...prev, [activeTool]: { status: 'error' } }));
      }
    );
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool]);

  function handleCopy() {
    if (state?.status !== 'loaded') return;
    navigator.clipboard.writeText(state.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <section>
      <div className="flex items-center gap-2 mb-1">
        <Terminal className="w-4 h-4 text-purple-600 dark:text-purple-400" />
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          SystemDox MCP preview
        </h2>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
        A live preview of what a connected assistant receives when it calls each MCP
        tool — built from your workspace standards, exactly as the server formats it.
      </p>

      {/* Tool picker */}
      <div className="flex flex-wrap gap-2 mb-3" role="tablist" aria-label="MCP tools">
        {MCP_TOOLS.map((tool) => (
          <button
            key={tool.name}
            role="tab"
            aria-selected={tool.name === activeTool}
            onClick={() => setActiveTool(tool.name)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-mono transition-colors',
              tool.name === activeTool
                ? tool.activeClass
                : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-700'
            )}
          >
            <tool.icon className="w-3.5 h-3.5" />
            {tool.name}
          </button>
        ))}
      </div>

      {/* Response frame */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
          <div className="min-w-0">
            <p className="text-xs font-mono text-slate-700 dark:text-slate-300 truncate">
              <span className="text-slate-400 dark:text-slate-500">tools/call → </span>
              {active.name}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{active.description}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <div className="flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden text-xs">
              {(['rendered', 'raw'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={cn(
                    'px-2.5 py-1 capitalize',
                    view === v
                      ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-medium'
                      : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
            <button
              onClick={handleCopy}
              disabled={state?.status !== 'loaded'}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
              title="Copy raw response"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="p-4 max-h-96 overflow-y-auto">
          {!state || state.status === 'loading' ? (
            <LoadingState size="md" />
          ) : state.status === 'error' ? (
            <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Couldn't load this preview — the standards API may be unavailable. Try again shortly.
            </div>
          ) : view === 'rendered' ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <MarkdownRenderer content={state.text} />
            </div>
          ) : (
            <pre className="text-xs font-mono whitespace-pre-wrap break-words text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-950 rounded-lg p-3 border border-slate-100 dark:border-slate-800">
              {state.text}
            </pre>
          )}
        </div>
      </div>
    </section>
  );
}
